import { ArrowLeft, ChevronDown, Loader2, Search } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ActiveOrg, User } from "@/schema";
import {
  activeContextStore,
  type OrgListItem,
} from "../services/ActiveContextStore";
import fetchClient from "../services/axiosInstance";
import { Tooltip } from "./Tooltip";

interface TenantSwitcherProps {
  user: User | null;
  activeOrg: ActiveOrg;
  compact?: boolean;
  onChanged?: () => void;
}

interface SwitchOrgResponse {
  orgId?: string;
  activeOrg?: ActiveOrg;
}

interface RequestError {
  message?: string;
  response?: { data?: { message?: string; error?: string } };
}

const requestErrorMessage = (error: unknown, fallback: string): string => {
  const requestError = error && typeof error === "object"
    ? error as RequestError
    : null;
  return requestError?.response?.data?.message
    || requestError?.response?.data?.error
    || requestError?.message
    || fallback;
};

export const TenantSwitcher = ({
  user,
  activeOrg: initialActiveOrg,
  compact = false,
  onChanged,
}: TenantSwitcherProps) => {
  const listKey = user?.id || "";
  const [availableOrgs, setAvailableOrgs] = useState<OrgListItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [switchingOrgId, setSwitchingOrgId] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const loadOrgs = useCallback(async (force = false) => {
    if (!user?.id) return;
    setIsLoading(true);
    setError(null);
    try {
      const items = await activeContextStore.orgs(listKey, force);
      setAvailableOrgs(items);
    } catch (error: unknown) {
      setError(requestErrorMessage(error, "Failed to load tenants."));
    } finally {
      setIsLoading(false);
    }
  }, [listKey, user?.id]);

  useEffect(() => {
    setAvailableOrgs([]);
    loadOrgs();
  }, [loadOrgs]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const filteredOrgs = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return availableOrgs;
    return availableOrgs.filter((org) =>
      String(org?.name || "").toLowerCase().includes(query)
      || String(org?.id || "").toLowerCase().includes(query));
  }, [availableOrgs, searchQuery]);

  // The authenticated response owns the active identity; the org list hydrates
  // only its label and never chooses or overrides it.
  const activeOrg = useMemo(() => {
    if (!initialActiveOrg) return null;
    const listedName = availableOrgs.find((org) => org.id === initialActiveOrg.id)?.name;
    return {
      ...initialActiveOrg,
      name: listedName || initialActiveOrg.name || initialActiveOrg.id,
    };
  }, [availableOrgs, initialActiveOrg]);

  const handleSwitchOrg = async (orgId: string) => {
    setSwitchingOrgId(orgId);
    setError(null);
    try {
      const response = await fetchClient.post("/org/switch", { orgId });
      const data = response.data as SwitchOrgResponse;
      const switchedOrgId = String(data.orgId || "").trim();
      if (switchedOrgId !== orgId || data.activeOrg?.id !== orgId) {
        throw new Error("Tenant switch returned a different active organization");
      }
      // Atomic selection: storage changes only after the outlet authorizes and
      // echoes the exact org. The reload then authenticates only into that org.
      activeContextStore.selectOrg(data.activeOrg);
      onChanged?.();
      window.location.reload();
    } catch (error: unknown) {
      setError(requestErrorMessage(error, "Failed to switch tenant."));
    } finally {
      setSwitchingOrgId(null);
    }
  };

  if (!user) return null;
  const canOpen = isLoading || !!error || availableOrgs.length > 0;
  const parentOrgId = activeOrg?.parentOrgId || null;
  const formatRole = (role?: string) => String(role || "member")
    .replace(/^org:/, "")
    .replace(/_/g, " ");

  return (
    <div ref={dropdownRef} className="relative flex items-center gap-1.5" data-switcher="organization">
      {parentOrgId && (
        <Tooltip
          label="Back to parent organization"
          description="Return through the same atomic organization switch and reload."
        >
          <button
            type="button"
            onClick={() => handleSwitchOrg(parentOrgId)}
            disabled={!!switchingOrgId}
            aria-label="Back to parent organization"
            className="shrink-0 flex items-center justify-center w-7 h-7 rounded-md border transition hover:opacity-85 disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ borderColor: "var(--border-secondary)", background: "transparent", color: "var(--text-primary)" }}
          >
            {switchingOrgId === parentOrgId
              ? <Loader2 className="w-3.5 h-3.5 animate-spin" style={{ color: "var(--text-secondary)" }} />
              : <ArrowLeft className="w-4 h-4" />}
          </button>
        </Tooltip>
      )}

      <div className={`relative ${compact ? "w-[150px]" : "w-[205px]"}`}>
        <button
          type="button"
          data-active-org-id={activeOrg?.id || undefined}
          onClick={() => {
            if (!isLoading) loadOrgs();
            if (canOpen) setOpen((value) => !value);
          }}
          disabled={isLoading && availableOrgs.length === 0}
          aria-haspopup={canOpen ? "menu" : undefined}
          aria-expanded={canOpen ? open : undefined}
          className="w-full flex items-center justify-between gap-2 px-3 py-1.5 rounded-md border transition disabled:opacity-60 disabled:cursor-not-allowed hover:opacity-85"
          style={{
            borderColor: "var(--border-secondary)",
            background: "transparent",
            color: "var(--text-primary)",
            cursor: canOpen ? undefined : "default",
          }}
        >
          <div className="min-w-0 text-left leading-[1.1]">
            <div className="text-[13px] font-medium truncate">{activeOrg.name || activeOrg.id}</div>
            {!compact && activeOrg?.orgRole && (
              <div className="text-[11px] truncate mt-0.5" style={{ color: "var(--text-muted)" }}>
                {formatRole(activeOrg.orgRole)}
                {activeOrg.managed || activeOrg.inherited ? " via managed tenant" : ""}
              </div>
            )}
          </div>
          {canOpen && (
            <ChevronDown
              className={`w-3.5 h-3.5 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
              style={{ color: "var(--text-muted)" }}
            />
          )}
        </button>

        {canOpen && open && (
          <div
            className="absolute right-0 top-full mt-2 z-50 w-[320px] max-w-[calc(100vw-2rem)] rounded-lg border bg-[var(--bg-secondary)] shadow-2xl overflow-hidden"
            style={{ borderColor: "var(--border-primary)" }}
            role="menu"
          >
            <div className="p-3 space-y-2" style={{ borderBottom: "1px solid var(--border-primary)" }}>
              <div className="flex items-center gap-2 rounded-lg px-3 py-2" style={{ background: "var(--bg-tertiary)", border: "1px solid var(--border-primary)" }}>
                <Search className="w-4 h-4" style={{ color: "var(--text-muted)" }} />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder="Search tenants"
                  className="bg-transparent border-none outline-none text-sm flex-1"
                  style={{ color: "var(--text-primary)" }}
                />
              </div>
              {error && <div className="text-xs text-red-400">{error}</div>}
            </div>

            <div className="max-h-64 overflow-y-auto">
              {filteredOrgs.map((org) => (
                <button
                  key={org.id}
                  type="button"
                  role="menuitem"
                  className="w-full text-left px-3 py-2 transition flex flex-col gap-0.5 hover:bg-[var(--bg-tertiary)]"
                  onClick={() => {
                    setOpen(false);
                    handleSwitchOrg(org.id);
                  }}
                  disabled={!!switchingOrgId || org.id === activeOrg?.id}
                >
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-sm font-semibold truncate" style={{ color: "var(--text-primary)" }}>
                      {org.name || org.id}
                    </span>
                    <div className="flex items-center gap-2 shrink-0">
                      {org.managed || org.inherited ? (
                        <span className="text-[10px] uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>Managed</span>
                      ) : null}
                      <span className="text-[10px] uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>
                        {formatRole(org.orgRole)}
                      </span>
                      {switchingOrgId === org.id ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    </div>
                  </div>
                  <span className="text-[11px] truncate" style={{ color: "var(--text-muted)" }}>{org.id}</span>
                </button>
              ))}
              {!isLoading && filteredOrgs.length === 0 && (
                <div className="text-sm text-center py-6" style={{ color: "var(--text-muted)" }}>
                  {searchQuery ? "No tenants match" : "No tenants found"}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
